import React, { useEffect, useState } from "react";
import useApi from "../../../CustomHooks/useApi";

import UserTable from "./UserTable";
import ChangeParentModal from "./ChangeParentModal";

const ROLES = [
    "DISTRIBUTOR",
    "MASTER_DISTRIBUTOR",
    "NATIONAL_HEAD",
    "MERCHANT",
    "INDEPENDENT_MERCHANT",
    "REGIONAL_SALES_MANAGER",
    "STATE_HEAD",
    "ZONAL_HEAD",
    "ASM",
];

const UserMapping = () => {
    const [users, setUsers] = useState([]);

    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedParent, setSelectedParent] = useState(null);

    const [roleUser, setRoleUser] = useState(null);
    const [newRole, setNewRole] = useState("");

    const [loading, setLoading] = useState(false);
    const [mappingResult, setMappingResult] = useState(null);
    const [roleUpdateResult, setRoleUpdateResult] = useState(null);

    const [keyword, setKeyword] = useState("");
    const [mobile, setMobile] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [showRoleResultOnly, setShowRoleResultOnly] = useState(false);
    const [showMappingResultOnly, setShowMappingResultOnly] = useState(false);

    const [isFetchingUsers, setIsFetchingUsers] = useState(true);



    const { callApi: getUsers } = useApi("/admin/users/get/all/new/v1/not-of-type", "GET");
    const { callApi: mapUsers } = useApi("/admin/users/map-users/new/v1/not-of-type", "POST");
    const { callApi: updateRole } = useApi(
        "/admin/users/update-role-userid/new/v1/not-of-type",
        "POST"
    );
    const { callApi: searchUsers } = useApi(
        "/admin/users/search/map-users/new/v1/not-of-type",
        "GET"
    );

    /* ================= FETCH USERS ================= */
    // const fetchUsers = async () => {
    //     const res = await getUsers();
    //     if (Array.isArray(res)) setUsers(res);
    // };
    const fetchUsers = async () => {
    setIsFetchingUsers(true); // Start loading
    try {
        const res = await getUsers();
        if (Array.isArray(res)) setUsers(res);
    } catch (error) {
        console.error("Failed to fetch users", error);
    } finally {
        setIsFetchingUsers(false); // Stop loading
    }
};

    useEffect(() => {
        fetchUsers();
    }, []);

    /* ================= CONFIRM MAP ================= */
    const confirmMapping = async () => {
        if (!selectedUser || !selectedParent) return;

        setLoading(true);
        try {
            const res = await mapUsers({
                parentUserId: selectedParent.userId,
                childUserIds: [selectedUser.userId],
            });

            // 🔥 STORE FULL RESPONSE
            setMappingResult(res);

            setShowMappingResultOnly(true);
        } catch (err) {
            setMappingResult({
                status: "FAILED",
                message:
                    err?.response?.data?.message || "Mapping failed",
            });
        } finally {
            setLoading(false);
        }
    };


    /* ================= UPDATE ROLE ================= */
    const confirmRoleUpdate = async () => {
        if (!roleUser || !newRole) return;

        setLoading(true);
        try {
            const res = await updateRole({
                targetUserId: roleUser.userId,
                newRole,
            });

            setRoleUpdateResult(res);
            setShowRoleResultOnly(true); // 🔥 SWITCH UI MODE
        } catch (err) {
            setRoleUpdateResult({
                status: "FAILED",
                message:
                    err?.response?.data?.message || "Role update failed",
            });
            setShowRoleResultOnly(true); // 🔥 even on failure
        } finally {
            setLoading(false);
        }
    };


    const normalizeSearchResponse = (res) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.content)) return res.content;
        return [];
    };

    const syncWithHierarchy = (list) => {
        const map = {};
        list.forEach(u => {
            map[u.userId] = u;
        });

        // Inject missing parents
        list.forEach(u => {
            if (u.parentId && !map[u.parentId]) {
                map[u.parentId] = {
                    userId: u.parentId,
                    username: u.parentFullName || u.parentShopName || u.parentId,
                    role: u.parentRole || "UNKNOWN",
                    mobile: u.parentMobile || "-",
                };
            }
        });

        return Object.values(map);
    };


    // const handleSearch = async () => {
    //     let result = [];

    //     const params = new URLSearchParams();
    //     if (keyword) params.append("keyword", keyword);
    //     if (mobile) params.append("mobile", mobile);
    //     if (roleFilter) params.append("role", roleFilter);

    //     const res = await searchUsers(null, `?${params.toString()}`);
    //     result = normalizeSearchResponse(res);

    //     // 🔥 VERY IMPORTANT
    //     setUsers(syncWithHierarchy(result));
    // };

    const handleSearch = async () => {
    setIsFetchingUsers(true); // Start loading
    try {
        let result = [];
        const params = new URLSearchParams();
        if (keyword) params.append("keyword", keyword);
        if (mobile) params.append("mobile", mobile);
        if (roleFilter) params.append("role", roleFilter);

        const res = await searchUsers(null, `?${params.toString()}`);
        result = normalizeSearchResponse(res);

        setUsers(syncWithHierarchy(result));
    } catch (error) {
        console.error("Failed to search users", error);
    } finally {
        setIsFetchingUsers(false); // Stop loading
    }
};



    return (
        <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">
                User Mapping & Role Management
            </h2>

            <div className="bg-white border rounded-xl p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">

                    {/* SEARCH MOBILE */}
                    <input
                        type="text"
                        placeholder="Search by mobile"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        className="border rounded px-3 py-2 text-sm"
                    />

                    {/* ROLE FILTER */}
                    <select
                        value={roleFilter}
                        onChange={(e) => {
                            const value = e.target.value;
                            setRoleFilter(value === "" ? "" : value);
                        }}
                        className="border rounded px-3 py-2 text-sm"
                    >
                        <option value="">All Roles</option>
                        {ROLES.map((r) => (
                            <option key={r} value={r}>
                                {r}
                            </option>
                        ))}
                    </select>

                    {/* SEARCH BUTTON */}
                    <button
                        onClick={handleSearch}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white rounded px-4 py-2 text-sm"
                    >
                        Search
                    </button>

                    {/* RESET */}
                    <button
                        onClick={() => {
                            setKeyword("");
                            setMobile("");
                            setRoleFilter("");
                            fetchUsers();
                        }}
                        className="bg-slate-200 hover:bg-slate-300 rounded px-3 py-2 text-sm"
                    >
                        Reset
                    </button>
                </div>

            </div>


            <UserTable
                users={users}
                isLoading={isFetchingUsers}
                onChangeParent={(user) => setSelectedUser(user)}
                onChangeRole={(user) => {
                    setRoleUser(user);
                    setNewRole(user.role);
                }}
            />

            {/* CHANGE PARENT */}
            {selectedUser && (
                <ChangeParentModal
                    user={selectedUser}
                    users={users}
                    loading={loading}
                    selectedParent={selectedParent}
                    onSelectParent={setSelectedParent}
                    mappingResult={mappingResult}
                    showResultOnly={showMappingResultOnly}
                    onConfirm={confirmMapping}
                    onClose={() => {
                        setSelectedUser(null);
                        setSelectedParent(null);
                        setMappingResult(null);
                        setShowMappingResultOnly(false);
                        fetchUsers();
                    }}
                />
            )}

            {/* CHANGE ROLE */}
            {roleUser && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white w-[420px] rounded-xl p-5">

                        <h3 className="text-lg font-semibold mb-3">
                            Change Role
                        </h3>

                        {/* ================= RESULT MODE ================= */}
                        {showRoleResultOnly ? (
                            <>
                                <div
                                    className={`border rounded-lg p-4 text-sm space-y-2 ${roleUpdateResult?.status === "SUCCESS"
                                        ? "border-green-300 bg-green-50 text-green-800"
                                        : "border-red-300 bg-red-50 text-red-800"
                                        }`}
                                >
                                    {/* MESSAGE */}
                                    <div className="font-semibold">
                                        {roleUpdateResult?.message}
                                    </div>

                                    {/* DETAILS */}
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mt-2">
                                        {roleUpdateResult?.mobile && (
                                            <>
                                                <div className="font-medium text-slate-600">Mobile</div>
                                                <div>{roleUpdateResult.mobile}</div>
                                            </>
                                        )}

                                        {roleUpdateResult?.oldRole && (
                                            <>
                                                <div className="font-medium text-slate-600">Old Role</div>
                                                <div>{roleUpdateResult.oldRole}</div>
                                            </>
                                        )}

                                        {roleUpdateResult?.newRole && (
                                            <>
                                                <div className="font-medium text-slate-600">New Role</div>
                                                <div className="font-semibold">{roleUpdateResult.newRole}</div>
                                            </>
                                        )}

                                        {roleUpdateResult?.oldUserId && (
                                            <>
                                                <div className="font-medium text-slate-600">Old User ID</div>
                                                <div className="truncate">{roleUpdateResult.oldUserId}</div>
                                            </>
                                        )}

                                        {roleUpdateResult?.newUserId && (
                                            <>
                                                <div className="font-medium text-slate-600">New User ID</div>
                                                <div className="truncate">{roleUpdateResult.newUserId}</div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="flex justify-end mt-5">
                                    <button
                                        onClick={() => {
                                            // RESET EVERYTHING
                                            setRoleUser(null);
                                            setNewRole("");
                                            setRoleUpdateResult(null);
                                            setShowRoleResultOnly(false);

                                            fetchUsers(); // 🔥 REFRESH USER LIST
                                        }}
                                        className="px-5 py-2 bg-indigo-600 text-white rounded"
                                    >
                                        OK
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* ================= FORM MODE ================= */}
                                <div className="text-sm mb-3">
                                    <b>User:</b> {roleUser.username}
                                </div>

                                <select
                                    className="w-full border rounded px-3 py-2"
                                    value={newRole}
                                    onChange={(e) => setNewRole(e.target.value)}
                                >
                                    {ROLES.map((r) => (
                                        <option key={r} value={r}>
                                            {r}
                                        </option>
                                    ))}
                                </select>

                                <div className="flex justify-end gap-3 mt-5">
                                    <button
                                        onClick={() => {
                                            setRoleUser(null);
                                            setNewRole("");
                                            setRoleUpdateResult(null);
                                        }}
                                        className="px-4 py-2 border rounded"
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        onClick={confirmRoleUpdate}
                                        disabled={loading}
                                        className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50"
                                    >
                                        {loading ? "Updating..." : "Update Role"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

        </div>
    );
};

export default UserMapping;
